import { DEFAULT_AUTOCLOSE_DELAY, MODAL_TYPES } from '@/legacy/constants/modalTypes';
import { useStore } from '@/legacy/store/zustand';
import { ModalComponentProps, ModalPropsMap } from '@/legacy/types/modal.types';
import { useEffect } from 'react';
import InfoModal from '../InfoModal';
import ConfirmModal from '../ConfirmModal';
import { CompanySwitchModal } from '@/features/auth/company-switch';

export const modalRegistry: {
	[K in keyof ModalPropsMap]: React.FC<ModalComponentProps<K>>;
} = {
	[MODAL_TYPES.NOTIFICATION_MODAL]: InfoModal,
	[MODAL_TYPES.COMPANY_SWITCH_MODAL]: CompanySwitchModal,
	[MODAL_TYPES.CONFIRM_MODAL]: ConfirmModal,
};

const ModalHost = () => {
	const modalType = useStore((s) => s.modalType);
	const modalProps = useStore((s) => s.modalProps);
	const closeModal = useStore((s) => s.closeModal);

	useEffect(() => {
		if (
			typeof modalProps === 'object' &&
			modalProps !== null &&
			'shouldAutoClose' in modalProps &&
			modalProps.shouldAutoClose
		) {
			const delay =
				'shouldAutoClose' in modalProps &&
				'autoCloseDelay' in modalProps &&
				typeof modalProps.autoCloseDelay === 'number'
					? modalProps.autoCloseDelay
					: DEFAULT_AUTOCLOSE_DELAY;

			const timeout = setTimeout(() => {
				closeModal();
			}, delay);

			return () => clearTimeout(timeout);
		}
	}, [modalProps, closeModal]);

	if (!modalType) return null;

	const renderModal = () => {
		switch (modalType) {
			case MODAL_TYPES.NOTIFICATION_MODAL: {
				const props = modalProps as ModalPropsMap[typeof MODAL_TYPES.NOTIFICATION_MODAL];
				return (
					<InfoModal
						{...props}
						onClose={closeModal}
						isOpen
					/>
				);
			}
			case MODAL_TYPES.COMPANY_SWITCH_MODAL: {
				const props = modalProps as ModalPropsMap[typeof MODAL_TYPES.COMPANY_SWITCH_MODAL];
				return (
					<CompanySwitchModal
						{...props}
						onClose={closeModal}
						isOpen
					/>
				);
			}
			case MODAL_TYPES.CONFIRM_MODAL: {
				const props = modalProps as ModalPropsMap[typeof MODAL_TYPES.CONFIRM_MODAL];
				return (
					<ConfirmModal
						{...props}
						onClose={closeModal}
						isOpen
					/>
				);
			}
			default:
				console.error(`Unknown modal type: ${String(modalType)}`);
				return null;
		}
	};

	return renderModal();
};

export default ModalHost;
